import { FC } from 'react'
import {
  Box,
  chakra,
  Flex,
  ListItem,
  Text,
  UnorderedList,
} from '@chakra-ui/react'
import { OptionCompany } from './hook'

const TextHidden = chakra('span', {
  baseStyle: {
    display: 'inline-block',
    textIndent: '-9999px',
    whiteSpace: 'pre',
  },
})

type CompanyAnalysisProps = {
  companyAnalysis: CompanyAnalysis
  selectedCompanies: OptionCompany[]
  report_type?: number
}

export enum ComparionCategory {
  SUMMARY_OVERVIEW = 'summary_overview',
  SUMMARY_SPECIAL_NOTE = 'summary_special_note',
  COMMON_OVERVIEW = 'common_overview',
  COMMON_INITIATIVE = 'common_initiative',
  DIFFERENCE_OVERVIEW = 'difference_overview',
  DIFFERENCE = 'difference',
  COMPARISON_ONLY_OVERVIEW = 'comparison_only_overview',
  COMPARISON_ONLY_INITIATIVE = 'comparison_only_initiative',
}

export type CompanyAnalysis = {
  companies: Company[]
  items: Item[]
}

type Company = {
  code: string
  name: string
  role: string
}

type Item = {
  category: string
  company_code: string
  content: string
  evidence: number[]
}

export const CompanyAnalysisContent: FC<CompanyAnalysisProps> = ({
  companyAnalysis,
  selectedCompanies,
  report_type,
}) => {
  function getCompany(company_code: string) {
    return selectedCompanies.find(x => x.company_code === company_code)
  }

  function getFirstItemByCategory(category: string) {
    return companyAnalysis.items.find(x => x.category === category)
  }

  function getItemsByCategory(category: string) {
    const filtered =
      companyAnalysis.items.filter(x => x.category === category) || []

    const grouped = filtered?.reduce(
      (acc, item) => {
        if (!acc[item.company_code]) acc[item.company_code] = []
        acc[item.company_code].push(item)
        return acc
      },
      {} as Record<string, Item[]>,
    )

    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([company_code, items]) => ({ company_code, items }))
  }

  function getCompanyNameByCode(code: string) {
    return companyAnalysis.companies.find(x => x.code === code)?.name
  }

  return (
    <>
      <Text>
        <TextHidden position={'absolute'}>共通点・差異分析結果</TextHidden>
        {selectedCompanies.map((company, index) => (
          <TextHidden
            key={`company_analysis_${company.company_id}_${index}`}
            position={'absolute'}
          >
            {company.company_code}: {company.company_name}
            {report_type === 4 && company.report_year
              ? ` (${company.report_year})`
              : ''}
          </TextHidden>
        ))}
      </Text>
      <Box>
        <Text fontWeight="700">1. 全体サマリー</Text>
        <Text>
          {getFirstItemByCategory(ComparionCategory.SUMMARY_OVERVIEW)?.content}
        </Text>
        <Text fontWeight="700" mt="24px">
          特記事項
        </Text>
        <UnorderedList pl={2}>
          {getItemsByCategory(
            ComparionCategory.SUMMARY_SPECIAL_NOTE,
          )[0].items.map(sn => (
            <ListItem>
              <Flex alignItems="center">
                <Text>
                  {sn.content}
                  <TextHidden>【引用元：</TextHidden>
                  <Text as={'span'}>
                    {sn.evidence.map((e, idx) => (
                      <>
                        <Text
                          key={`${e}-${idx}`}
                          alignItems="center"
                          as={'span'}
                          backgroundColor="#6FB0ED"
                          borderRadius="24px"
                          color="#fff"
                          cursor="pointer"
                          display="inline"
                          height="22px"
                          justifyContent="center"
                          ml="8px"
                          px="3px"
                          py="3px"
                          onClick={() => {
                            window.open(
                              `${
                                window.location.origin
                              }/dashboard/post/${getCompany(sn.company_code)
                                ?.news_id}?list_news_ai_id=${getCompany(
                                sn.company_code,
                              )?.list_news_ai_id}&company_id=${getCompany(
                                sn.company_code,
                              )?.company_id}&tab=0&block=${e}`,
                              '_blank',
                            )
                          }}
                        >
                          <Text
                            alignItems="center"
                            as={'span'}
                            bg="white"
                            borderRadius="12px"
                            color="#6FB0ED"
                            height="100%"
                            justifyContent="center"
                            lineHeight="5px"
                            px="6px"
                          >
                            {sn.company_code}
                          </Text>
                          <Text
                            alignItems="center"
                            as={'span'}
                            height="100%"
                            justifyContent="center"
                            lineHeight="5px"
                            px="3px"
                          >
                            {e}
                          </Text>
                        </Text>
                        {idx < sn.evidence.length - 1 && (
                          <TextHidden>,</TextHidden>
                        )}
                      </>
                    ))}
                  </Text>
                  <TextHidden>】</TextHidden>
                </Text>
              </Flex>
            </ListItem>
          ))}
        </UnorderedList>
      </Box>

      <Box>
        <Text fontWeight="700" mt="24px">
          2. 共通の取り組み
        </Text>
        <Text>
          {getFirstItemByCategory(ComparionCategory.COMMON_OVERVIEW)?.content}
        </Text>
        {getItemsByCategory(ComparionCategory.COMMON_INITIATIVE).map(cc => (
          <>
            <Text fontWeight="700" mt="24px">
              {getCompanyNameByCode(cc.company_code)}({cc.company_code}):
            </Text>
            <UnorderedList pl={2}>
              {cc.items.map(sn => (
                <ListItem>
                  <Flex alignItems="center">
                    <Text>
                      {sn.content}
                      <TextHidden>【引用元：</TextHidden>
                      {sn.evidence.map((e, idx) => (
                        <Text key={`${e}-${idx}`} as={'span'}>
                          <Text
                            alignItems="center"
                            as={'span'}
                            backgroundColor="#6FB0ED"
                            borderRadius="24px"
                            color="#fff"
                            cursor="pointer"
                            display="inline"
                            height="22px"
                            justifyContent="center"
                            ml="8px"
                            px="3px"
                            py="3px"
                            onClick={() => {
                              window.open(
                                `${
                                  window.location.origin
                                }/dashboard/post/${getCompany(cc.company_code)
                                  ?.news_id}?list_news_ai_id=${getCompany(
                                  cc.company_code,
                                )?.list_news_ai_id}&company_id=${getCompany(
                                  cc.company_code,
                                )?.company_id}&tab=0&block=${e}`,
                                '_blank',
                              )
                            }}
                          >
                            <Text
                              alignItems="center"
                              as={'span'}
                              bg="white"
                              borderRadius="12px"
                              color="#6FB0ED"
                              height="100%"
                              justifyContent="center"
                              lineHeight="5px"
                              px="6px"
                            >
                              {sn.company_code}
                            </Text>
                            <Text
                              alignItems="center"
                              as={'span'}
                              height="100%"
                              justifyContent="center"
                              lineHeight="5px"
                              px="3px"
                            >
                              {e}
                            </Text>
                          </Text>
                          {idx < sn.evidence.length - 1 && (
                            <TextHidden>,</TextHidden>
                          )}
                        </Text>
                      ))}
                      <TextHidden>】</TextHidden>
                    </Text>
                  </Flex>
                </ListItem>
              ))}
            </UnorderedList>
          </>
        ))}
      </Box>

      <Box>
        <Text fontWeight="700" mt="24px">
          3. 特徴的な差異
        </Text>
        <Text>
          {
            getFirstItemByCategory(ComparionCategory.DIFFERENCE_OVERVIEW)
              ?.content
          }
        </Text>
        {getItemsByCategory(ComparionCategory.DIFFERENCE).map(cc => (
          <>
            <Text fontWeight="700" mt="24px">
              {getCompanyNameByCode(cc.company_code)}({cc.company_code}):
            </Text>
            <UnorderedList pl={2}>
              {cc.items.map(sn => (
                <ListItem>
                  <Flex alignItems="center">
                    <Text>
                      {sn.content}
                      <TextHidden>【引用元：</TextHidden>
                      {sn.evidence.map((e, idx) => (
                        <Text key={`${e}-${idx}`} as={'span'}>
                          <Text
                            alignItems="center"
                            as={'span'}
                            backgroundColor="#6FB0ED"
                            borderRadius="24px"
                            color="#fff"
                            cursor="pointer"
                            display="inline"
                            height="22px"
                            justifyContent="center"
                            ml="8px"
                            px="3px"
                            py="3px"
                            onClick={() => {
                              window.open(
                                `${
                                  window.location.origin
                                }/dashboard/post/${getCompany(cc.company_code)
                                  ?.news_id}?list_news_ai_id=${getCompany(
                                  cc.company_code,
                                )?.list_news_ai_id}&company_id=${getCompany(
                                  cc.company_code,
                                )?.company_id}&tab=0&block=${e}`,
                                '_blank',
                              )
                            }}
                          >
                            <Text
                              alignItems="center"
                              as={'span'}
                              bg="white"
                              borderRadius="12px"
                              color="#6FB0ED"
                              height="100%"
                              justifyContent="center"
                              lineHeight="5px"
                              px="6px"
                            >
                              {sn.company_code}
                            </Text>
                            <Text
                              alignItems="center"
                              as={'span'}
                              height="100%"
                              justifyContent="center"
                              lineHeight="5px"
                              px="3px"
                            >
                              {e}
                            </Text>
                          </Text>
                          {idx < sn.evidence.length - 1 && (
                            <TextHidden>,</TextHidden>
                          )}
                        </Text>
                      ))}
                      <TextHidden>】</TextHidden>
                    </Text>
                  </Flex>
                </ListItem>
              ))}
            </UnorderedList>
          </>
        ))}
      </Box>

      <Box>
        <Text fontWeight="700" mt="24px">
          4. 比較企業のみの取り組み
        </Text>
        <Text>
          {
            getFirstItemByCategory(ComparionCategory.COMPARISON_ONLY_OVERVIEW)
              ?.content
          }
        </Text>
        {getItemsByCategory(ComparionCategory.COMPARISON_ONLY_INITIATIVE).map(
          cc => (
            <>
              <Text fontWeight="700" mt="24px">
                {getCompanyNameByCode(cc.company_code)}({cc.company_code}):
              </Text>
              <UnorderedList pl={2}>
                {cc.items.map(sn => (
                  <ListItem>
                    <Flex alignItems="center">
                      <Text>
                        {sn.content}
                        <TextHidden>【引用元：</TextHidden>
                        {sn.evidence.map((e, idx) => (
                          <Text key={`${e}-${idx}`} as={'span'}>
                            <Text
                              alignItems="center"
                              as={'span'}
                              backgroundColor="#6FB0ED"
                              borderRadius="24px"
                              color="#fff"
                              cursor="pointer"
                              display="inline"
                              height="22px"
                              justifyContent="center"
                              ml="8px"
                              px="3px"
                              py="3px"
                              onClick={() => {
                                window.open(
                                  `${
                                    window.location.origin
                                  }/dashboard/post/${getCompany(cc.company_code)
                                    ?.news_id}?list_news_ai_id=${getCompany(
                                    cc.company_code,
                                  )?.list_news_ai_id}&company_id=${getCompany(
                                    cc.company_code,
                                  )?.company_id}&tab=0&block=${e}`,
                                  '_blank',
                                )
                              }}
                            >
                              <Text
                                alignItems="center"
                                as={'span'}
                                bg="white"
                                borderRadius="12px"
                                color="#6FB0ED"
                                height="100%"
                                justifyContent="center"
                                lineHeight="5px"
                                px="6px"
                              >
                                {sn.company_code}
                              </Text>
                              <Text
                                alignItems="center"
                                as={'span'}
                                height="100%"
                                justifyContent="center"
                                lineHeight="5px"
                                px="3px"
                              >
                                {e}
                              </Text>
                            </Text>
                            {idx < sn.evidence.length - 1 && (
                              <TextHidden>,</TextHidden>
                            )}
                          </Text>
                        ))}
                        <TextHidden>】</TextHidden>
                      </Text>
                    </Flex>
                  </ListItem>
                ))}
              </UnorderedList>
            </>
          ),
        )}
      </Box>
    </>
  )
}
